/**
 * Buddy Group Setup Page (rodeo-level).
 *
 * Single primary entry point for managing the user's buddy group at a
 * rodeo. Three states are handled inline so the user never has to drop
 * into the per-event registration flow just to set up a group:
 *
 *   1. No group yet  → render a "Create Buddy Group" form. Caller becomes
 *      primary cardholder on submit.
 *   2. Group exists  → render member management (invite / remove via the
 *      existing BuddyGroupManageMembers component) AND the rodeo-level
 *      performance preferences form.
 *   3. Read-only     → non-primary members see the same view but cannot
 *      edit preferences or members. Primary sees a frozen state once
 *      any draw has run.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Lock,
  Plus,
  Save,
  UserPlus,
  Users,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  PerformancePreferenceStep,
  type PerformanceChoice,
} from '@/components/events/PerformancePreferenceStep';
import { BuddyGroupManageMembers } from '@/components/buddy-groups/BuddyGroupManageMembers';
import { WithdrawBuddyGroupModal } from '@/components/buddy-groups/WithdrawBuddyGroupModal';
import { useBuddyGroup } from '@/hooks/useBuddyGroup';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { httpClient } from '@/api/http-client';
import {
  createBuddyGroup,
  getMyGroup,
  setBuddyGroupPreferences,
  getBuddyGroupPreferencesEditable,
  attachBuddyGroupToRodeo,
  listMyBuddyGroups,
  getBuddyGroupMembersWithNames,
} from '@/services/buddy-group.service';
import type {
  BuddyGroupDetail,
  BuddyGroupMemberView,
  BuddyGroupSummary,
} from '@/types/buddy-groups';

interface RodeoPerformanceRow {
  id: string;
  performance_name: string | null;
  is_slack: boolean | null;
  start_datetime: string | null;
}

interface RodeoSummary {
  id: string;
  name: string;
  buddy_groups_enabled: boolean;
  /** `rodeos.is_finals` — buddy groups are not offered on a finals rodeo. */
  is_finals: boolean;
  preference_config: {
    ifPerformance: ('slack' | 'out' | 'performance')[];
    ifSlack: ('slack' | 'out' | 'performance')[];
  } | null;
  entry_close_time: string | null;
  end_date: string | null;
}

const DEFAULT_PERFORMANCE_NAMES = ['Performance 1', 'Performance 2', 'Performance 3'];

/** Strip the disambiguation `[<isoDatetime>]` suffix the picker now appends. */
function preferenceHead(value: string | null | undefined): string {
  if (!value) return '';
  return value.trim().replace(/\s*\[[^\]]*\]\s*$/, '');
}

/**
 * Promote a stored preference value to the version that exists in
 * `options`. Existing buddy groups have values like "Performance 1" or
 * "Slack" (no datetime suffix); after the picker upgrade the matching
 * option lives at "Performance 1 [<iso>]". We match on the name head so
 * the dropdown shows the user's prior pick instead of going blank.
 */
function resolveLegacyPreferenceValue(
  stored: string | null | undefined,
  options: PerformanceChoice[],
): string {
  if (!stored) return '';
  if (options.some((o) => o.value === stored)) return stored;
  const head = preferenceHead(stored);
  if (!head) return stored;
  const candidate = options.find((o) => preferenceHead(o.value) === head);
  return candidate ? candidate.value : stored;
}

/**
 * Render a performance's start datetime as a short, locale-aware label
 * (e.g. "Fri, May 15, 6:00 PM"). Returns an empty string when the input
 * is missing or unparseable so callers can fall back to a name-only
 * label without rendering "Invalid Date".
 */
function formatPerformanceDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Inline invitation panel rendered directly inside a buddy-group row on the
 * "Register Buddy Group" screen. Reuses the canonical
 * BuddyGroupManageMembers component (the same widget the rodeo-attached
 * group view uses) so that search-and-invite UX stays consistent.
 *
 * This unblocks the dead-end the user hit before this fix: a freshly
 * created single-member buddy group used to render greyed-out with no path
 * forward — the only way to invite was to leave for "Manage Buddy Groups".
 * Now the panel opens in place so they can invite without losing their
 * registration context.
 */
function InlineBuddyGroupInvitePanel({
  groupId,
  currentUserProfileId,
  rodeoId,
  onInvitationsChanged,
}: {
  groupId: string;
  currentUserProfileId: string;
  rodeoId: string;
  /** Fired whenever the panel mutates members so the parent row can
   *  refresh its accepted/pending counts. */
  onInvitationsChanged: () => void;
}) {
  const { data: detail, isLoading, isError, error } = useBuddyGroup(groupId);

  // Detail invalidation already happens inside useSendInvitation /
  // useCancelInvitation, but the parent row's summary counts come from a
  // separate non-React-Query fetch (listMyBuddyGroups). Bubble a "something
  // changed" signal up so the parent can refresh those counts.
  const memberCount = detail?.members.length ?? null;
  useEffect(() => {
    if (memberCount === null) return;
    onInvitationsChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberCount]);

  if (isLoading) {
    return (
      <div className="rounded-md border border-dashed p-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading members…
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        {(error as Error | undefined)?.message ?? 'Failed to load group members.'}
      </div>
    );
  }

  const isPrimaryUser =
    detail.primary_member_user_profile_id === currentUserProfileId;

  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <BuddyGroupManageMembers
        groupId={detail.id}
        rodeoId={rodeoId}
        members={detail.members}
        currentUserProfileId={currentUserProfileId}
        isPrimaryUser={isPrimaryUser}
      />
    </div>
  );
}

export default function BuddyGroupSetupPage() {
  const { rodeoId } = useParams<{ rodeoId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const userProfileId = useAuthStore((s) => s.profile?.id ?? null);
  const queryClient = useQueryClient();

  // Invalidate every cache that mirrors buddy-group attachment or
  // event-registration state. Called after attach/detach so the
  // /rodeos card grid, the rodeo detail page, and any open my-group
  // views all refetch instead of showing the pre-mutation snapshot.
  // Detach in particular unlinks event_registrations from the group,
  // which moves them out of the "group has X registrations" line.
  const invalidateRodeoCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['buddy-group-ops'] });
    queryClient.invalidateQueries({ queryKey: ['registration-ops'] });
    queryClient.invalidateQueries({ queryKey: ['buddy-groups'] });
    queryClient.invalidateQueries({
      queryKey: ['buddy-group-rodeo-attachments'],
    });
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [group, setGroup] = useState<BuddyGroupDetail | null>(null);
  const [rodeo, setRodeo] = useState<RodeoSummary | null>(null);
  const [performanceOptions, setPerformanceOptions] = useState<PerformanceChoice[]>([]);
  const [editable, setEditable] = useState<{ allowed: boolean; reason: string | null }>({
    allowed: true,
    reason: null,
  });
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [otherGroups, setOtherGroups] = useState<BuddyGroupSummary[]>([]);
  // Per-group accepted member rosters (id → list). Drives the "members"
  // line on each existing-group row so the user can identify who is
  // actually in each group before picking one to register. Populated
  // after `otherGroups` loads with one /buddy-group-members + one
  // /user-profiles call per member; a missing/failed lookup leaves the
  // entry undefined and the row falls back to the count line.
  const [groupMembers, setGroupMembers] = useState<
    Record<string, BuddyGroupMemberView[]>
  >({});
  const [attachingId, setAttachingId] = useState<string | null>(null);
  // Group queued for withdrawal from this rodeo. Non-null opens the
  // confirmation modal; both the header button and the group-picker rows
  // feed it so the warning copy can never diverge between surfaces.
  const [withdrawTarget, setWithdrawTarget] = useState<{
    id: string;
    name: string | null;
  } | null>(null);
  const [pendingInvites, setPendingInvites] = useState<BuddyGroupMemberView[]>([]);
  // Which row in the existing-groups list has its inline invite panel
  // expanded. Only one expands at a time so the page stays compact. Set
  // automatically to the new group's id after handleCreate so the user
  // can invite without having to reach for a "Manage" link.
  const [expandedInviteGroupId, setExpandedInviteGroupId] = useState<
    string | null
  >(null);

  /** Refresh the user's existing-groups list. Used after create / invite /
   *  cancel mutations so the row counts (accepted, pending) stay live. */
  const refreshOtherGroups = async () => {
    if (!userProfileId) return;
    try {
      const all = await listMyBuddyGroups(userProfileId);
      const eligible = all.filter(
        (g) => g.is_primary && g.status !== 'disbanded',
      );
      setOtherGroups(eligible);
      // Re-pull rosters too so a freshly accepted invite (or a removed
      // member) shows on the row without a hard refresh.
      for (const g of eligible) {
        getBuddyGroupMembersWithNames(g.id)
          .then((members) => {
            setGroupMembers((prev) => ({ ...prev, [g.id]: members }));
          })
          .catch(() => {
            // Best-effort.
          });
      }
    } catch {
      // Best-effort enrichment — leave the list as-is.
    }
  };

  /** Pull pending invitations for the current group so they count
   *  toward the 4-member cap and render alongside accepted members. */
  const loadPendingInvites = async (groupId: string) => {
    try {
      const inv = await httpClient.get<{
        data: Array<{
          id: string;
          buddy_group_id: string;
          invitee_user_profile_id: string;
          status: string;
        }>;
      }>(`/buddy-group-invitations?buddy_group_id=${groupId}&status=pending&limit=20`);
      const pending = (inv?.data ?? []).filter((i) => i.status === 'pending');
      const enriched: BuddyGroupMemberView[] = await Promise.all(
        pending.map(async (i) => {
          let displayName: string | null = null;
          try {
            const profileRes = await httpClient.get<{
              data: {
                display_first_name?: string | null;
                display_last_name?: string | null;
                first_name?: string | null;
                last_name?: string | null;
              };
            }>(`/user-profiles/${i.invitee_user_profile_id}`);
            const p = profileRes?.data;
            const first = p?.display_first_name ?? p?.first_name ?? '';
            const last = p?.display_last_name ?? p?.last_name ?? '';
            displayName = [first, last].filter(Boolean).join(' ') || null;
          } catch {
            // ignore — fall back to "Pending invitee"
          }
          return {
            id: i.id,
            user_profile_id: i.invitee_user_profile_id,
            is_primary: false,
            display_name: displayName,
            member_number: null,
            invitation_status: 'pending' as const,
            invitation_id: i.id,
          };
        }),
      );
      setPendingInvites(enriched);
    } catch {
      setPendingInvites([]);
    }
  };

  const refreshGroup = async () => {
    if (!rodeoId) return;
    const grp = await getMyGroup(rodeoId);
    setGroup(grp);
    if (grp) {
      setFirst(resolveLegacyPreferenceValue(grp.preferences_first, performanceOptions));
      setSecond(resolveLegacyPreferenceValue(grp.preferences_second, performanceOptions));
      try {
        const ed = await getBuddyGroupPreferencesEditable(grp.id, rodeoId);
        setEditable(ed);
      } catch {
        // editable check is non-critical
      }
      await loadPendingInvites(grp.id);
    } else {
      setPendingInvites([]);
    }
    return grp;
  };

  // Load: my group + rodeo + performances + edit-gating.
  useEffect(() => {
    if (!rodeoId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [grp, rodeoRow, perfRow] = await Promise.all([
          getMyGroup(rodeoId),
          httpClient
            .get<{
              data: {
                id: string;
                name: string;
                buddy_groups_enabled?: boolean;
                is_finals?: boolean;
                preference_config: any;
              };
            }>(`/rodeos/${rodeoId}`)
            .then((r) => r?.data ?? null),
          // The legacy `rodeo_performances` table was dropped — performances
          // now live in the unified `performances` table keyed by
          // trig_rodeo_id. Hitting the old endpoint returned 404 and left the
          // preference dropdown with only the hardcoded "Slack" option,
          // hiding every named performance from the user.
          httpClient
            .get<{ data: RodeoPerformanceRow[] }>(
              `/performances?trig_rodeo_id=${rodeoId}&limit=50`
            )
            .then((r) => r?.data ?? [])
            .catch(() => [] as RodeoPerformanceRow[]),
        ]);
        if (cancelled) return;
        setGroup(grp);
        if (rodeoRow) {
          setRodeo({
            id: rodeoRow.id,
            name: rodeoRow.name,
            buddy_groups_enabled: !!rodeoRow.buddy_groups_enabled,
            is_finals: rodeoRow.is_finals === true,
            preference_config: rodeoRow.preference_config ?? null,
            entry_close_time: (rodeoRow as any).entry_close_time ?? null,
            end_date: (rodeoRow as any).end_date ?? null,
          });
        }

        // Build the option list shown in the preference dropdowns. Every
        // performance row from the unified `performances` table — named
        // OR slack — becomes its own selectable option, ordered by
        // start_datetime so users see them in run order. The stored
        // value carries a trailing `[<isoDatetime>]` suffix so that two
        // rows sharing a name (e.g. two "Performance 1"s on different
        // days) round-trip as distinct preferences; backend
        // `categorizePreference` strips the suffix before category
        // matching, so this is transparent to the draw resolver.
        //
        // We still de-dupe by `(name, start_datetime)` because the
        // unified table can repeat the same physical performance once
        // per event slot — those are the SAME run, not separate options.
        const seen = new Set<string>();
        const richOptions: PerformanceChoice[] = [...perfRow]
          .filter((p) => !!p.performance_name)
          .sort((a, b) => {
            const aTime = a.start_datetime ? Date.parse(a.start_datetime) : 0;
            const bTime = b.start_datetime ? Date.parse(b.start_datetime) : 0;
            return aTime - bTime;
          })
          .reduce<PerformanceChoice[]>((acc, p) => {
            const name = p.performance_name as string;
            const isSlack = !!p.is_slack;
            const isoDate = p.start_datetime ?? '';
            const dedupeKey = `${isSlack ? 'slack' : 'perf'}::${name}::${isoDate}`;
            if (seen.has(dedupeKey)) return acc;
            seen.add(dedupeKey);
            const baseValue = isSlack ? 'Slack' : name;
            const value = isoDate ? `${baseValue} [${isoDate}]` : baseValue;
            const dateLabel = formatPerformanceDate(p.start_datetime);
            const labelName = isSlack && name.toLowerCase() === 'slack' ? 'Slack' : name;
            const label = dateLabel
              ? `${labelName} — ${dateLabel}`
              : labelName;
            acc.push({ value, label, isSlack });
            return acc;
          }, []);

        const finalOptions: PerformanceChoice[] =
          richOptions.length === 0
            ? DEFAULT_PERFORMANCE_NAMES.map((name) => ({
                value: name,
                label: name,
                isSlack: false,
              })).concat([{ value: 'Slack', label: 'Slack', isSlack: true }])
            : richOptions;
        setPerformanceOptions(finalOptions);

        if (grp) {
          // Promote legacy stored values ("Performance 1", "Slack") to the
          // matching disambiguated option ("Performance 1 [<iso>]") so the
          // dropdown reflects the user's prior pick.
          setFirst(resolveLegacyPreferenceValue(grp.preferences_first, finalOptions));
          setSecond(resolveLegacyPreferenceValue(grp.preferences_second, finalOptions));
          const ed = await getBuddyGroupPreferencesEditable(grp.id, rodeoId);
          if (!cancelled) setEditable(ed);
          await loadPendingInvites(grp.id);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? 'Failed to load buddy group');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rodeoId]);

  // Eligible groups load is intentionally split from the rodeo/group load
  // above. It must not be gated on getMyGroup — when userProfileId hydrates
  // a tick after rodeoId, gating would silently no-op and the picker would
  // never appear, leaving the user stuck on the create-only view.
  //
  // Buddy groups are M:N with rodeos: the same group can be registered for
  // any number of rodeos at once, so we surface every primary, non-disbanded
  // group regardless of other attachments. Per-row we mark whether the
  // group is already attached to THIS rodeo so we can render Unregister
  // instead of Register-for-this-rodeo.
  useEffect(() => {
    if (!rodeoId || !userProfileId) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await listMyBuddyGroups(userProfileId);
        if (cancelled) return;
        const eligible = all.filter(
          (g) => g.is_primary && g.status !== 'disbanded',
        );
        setOtherGroups(eligible);
        // Roster enrichment runs in parallel — render falls back to the
        // count line until each row resolves, so a slow profile lookup
        // doesn't block the whole picker. We dispatch one setState per
        // group to stream rows in as they arrive instead of holding the
        // entire list until the slowest member call returns.
        for (const g of eligible) {
          getBuddyGroupMembersWithNames(g.id)
            .then((members) => {
              if (cancelled) return;
              setGroupMembers((prev) => ({ ...prev, [g.id]: members }));
            })
            .catch(() => {
              // Best-effort — leave row to its count fallback.
            });
        }
      } catch {
        // Best-effort enrichment — empty list is fine.
      }
    })();
    return () => {
      cancelled = true;
    };
    // refreshOtherGroups identity is stable enough; we only re-run on
    // identifier changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rodeoId, userProfileId]);

  const isPrimary = useMemo(
    () => !!group && !!userProfileId && group.primary_member_user_profile_id === userProfileId,
    [group, userProfileId]
  );

  const lockedAt = group?.preferences_locked_at ?? null;
  const readOnly = !isPrimary || !editable.allowed;

  // Withdrawal runs through WithdrawBuddyGroupModal rather than a bare
  // window.confirm: the captain has to see how many entries become solo
  // entries (and that none of them are cancelled) before committing.
  const handleUnregister = () => {
    if (!group || !rodeoId) return;
    setWithdrawTarget({ id: group.id, name: group.group_name ?? null });
  };

  const handleAttach = async (groupId: string) => {
    if (!rodeoId) return;
    setCreateError(null);
    setAttachingId(groupId);
    try {
      const result = await attachBuddyGroupToRodeo(groupId, rodeoId);
      const decoupled = result.decoupled_partnered_entries ?? [];
      // Per Option A, member roping entries with an outside partner stay
      // decoupled — the entry is governed by the pair, not the group.
      // Surface them on the success toast so the primary knows up front
      // which entries won't follow the group's draw preferences.
      if (decoupled.length > 0) {
        const summary = decoupled
          .slice(0, 3)
          .map((d) => {
            const member = d.member_name ?? 'A member';
            const partner = d.partner_name ?? 'an outside partner';
            const event = d.event_name ?? 'a team roping event';
            return `${member} + ${partner} (${event})`;
          })
          .join('; ');
        const more =
          decoupled.length > 3 ? ` …and ${decoupled.length - 3} more.` : '';
        showSuccess(
          'Buddy group attached',
          `This group is now bound to the rodeo. Heads up — ${decoupled.length} team roping pair${
            decoupled.length === 1 ? '' : 's'
          } involve someone outside the group, so those entries will be drawn separately: ${summary}.${more}`,
        );
      } else {
        showSuccess(
          'Buddy group attached',
          'This group is now bound to the rodeo. Manage members and lock preferences below.',
        );
      }
      invalidateRodeoCaches();
      await refreshGroup();
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to attach buddy group';
      setCreateError(msg);
      showError('Could not attach group', msg);
    } finally {
      setAttachingId(null);
    }
  };

  const handleCreate = async () => {
    if (!rodeoId) return;
    if (!newGroupName.trim()) {
      setCreateError('Give your buddy group a name.');
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      // Important: do NOT pass rodeo_id here. New groups must start
      // unattached so the user has to invite + accept members BEFORE
      // they can register the group for the rodeo (the 2-accepted-member
      // minimum is enforced server-side in attachGroupToRodeo). If we
      // auto-attached, a fresh 1-person group would slip past the gate.
      const created = await createBuddyGroup({
        group_name: newGroupName.trim(),
      });
      showSuccess(
        'Buddy group created',
        "Now invite members below. Once at least one other member accepts, you'll be able to enter this group in the rodeo.",
      );
      setNewGroupName('');
      // Reload the eligible-groups list so the new group shows up below.
      await refreshOtherGroups();
      // Auto-expand the new group's invite panel so the user has a
      // direct, in-page path to invite members instead of having to
      // navigate away to "Manage Buddy Groups".
      const newId = created?.buddy_group?.id;
      if (newId) {
        setExpandedInviteGroupId(newId);
      }
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to create buddy group';
      setCreateError(msg);
      showError('Create failed', msg);
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!group) return;
    if (!first) {
      setError('Pick a 1st performance preference.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const result = await setBuddyGroupPreferences(group.id, rodeoId!, {
        preferences_first: first,
        preferences_second: second || null,
      });
      const msg =
        result.backfilled_registration_count > 0
          ? `Preferences locked for the group. ${result.backfilled_registration_count} existing entr${result.backfilled_registration_count === 1 ? 'y' : 'ies'} updated.`
          : 'Preferences locked for the group.';
      showSuccess('Buddy group preferences saved', msg);
      await refreshGroup();
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to save preferences';
      setError(msg);
      showError('Save failed', msg);
    } finally {
      setSaving(false);
    }
  };

  // Rendered in both page states (picker + attached group) so whichever
  // "Withdraw" affordance the user reaches, they get the same preview and
  // the same warning.
  const withdrawModal =
    withdrawTarget && rodeoId ? (
      <WithdrawBuddyGroupModal
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setWithdrawTarget(null);
        }}
        groupId={withdrawTarget.id}
        groupName={withdrawTarget.name}
        rodeoId={rodeoId}
        onWithdrawn={() => {
          const wasAttachedGroup = group?.id === withdrawTarget.id;
          setWithdrawTarget(null);
          invalidateRodeoCaches();
          if (wasAttachedGroup) {
            // This page's whole subject just went away — drop back to
            // the rodeo rather than render a stale group.
            navigate(`/rodeos/${rodeoId}`);
          } else {
            void refreshOtherGroups();
          }
        }}
      />
    ) : null;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // Finals rodeos run every contestant in every performance, so there is
  // nothing for a buddy group to co-schedule. The backend rejects create /
  // join / attach / set-preferences for these rodeos; render the reason
  // instead of a form the user can't submit.
  if (rodeo?.is_finals) {
    return (
      <div className="space-y-6 max-w-2xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/rodeos/${rodeoId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Rodeo
        </Button>

        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Buddy Group
          </p>
          <h1 className="text-3xl font-bold tracking-tight">
            {rodeo?.name ?? 'This rodeo'}
          </h1>
        </div>

        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            Buddy groups are not available for a finals rodeo. Every
            contestant runs in every performance, so there is no performance
            to be scheduled together for.
          </CardContent>
        </Card>
      </div>
    );
  }

  // No group yet → render the create form (does double-duty as the
  // "register your buddy group for this rodeo" entry point).
  if (!group) {
    return (
      <div className="space-y-6 max-w-2xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/rodeos/${rodeoId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Rodeo
        </Button>

        <div className="space-y-1">
          {/* Rodeo name leads — that's the page's primary context.
              The action label ("Enter Buddy Group") drops to a small
              uppercase eyebrow above so the user knows what they're
              doing here without burying the rodeo identity in body
              copy. */}
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Enter Buddy Group
          </p>
          <h1 className="text-3xl font-bold tracking-tight">
            {rodeo?.name ?? 'This rodeo'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {otherGroups.length > 0
              ? 'Pick one of your existing groups below, or create a new one. '
              : ''}
            You&apos;ll be the primary cardholder and choose the 1st &amp; 2nd
            performance preferences once for the entire group — those values
            then apply to every event your members enter.
          </p>
          <p className="text-xs text-muted-foreground">
            If any group member already has an individual entry for an
            event in this rodeo, it will be converted to a buddy-group
            entry automatically — and the preferences you choose for
            the group take over from any individual preferences they had set.
          </p>
        </div>

        {(() => {
          // Two block-level gates that hide BOTH the reuse-existing-group
          // card and the create-new-group card, replacing them with a
          // single closed-state notice. Mirrors the per-event gate on
          // EventDetailPage and the server-side ENTRY_CLOSE_PASSED refusal.
          const closeAtRaw = rodeo?.entry_close_time ?? null;
          const closeMs = closeAtRaw ? new Date(closeAtRaw).getTime() : NaN;
          const entriesHaveClosed =
            Number.isFinite(closeMs) && closeMs <= Date.now();
          // End-of-day comparison so a rodeo whose end_date is today still
          // counts as in-progress.
          const endRaw = rodeo?.end_date ?? null;
          const endMs = endRaw ? new Date(`${endRaw}T23:59:59`).getTime() : NaN;
          const rodeoCompleted =
            Number.isFinite(endMs) && endMs < Date.now();

          if (rodeoCompleted) {
            return (
              <Card>
                <CardContent className="py-6">
                  <p className="text-sm text-muted-foreground text-center">
                    This rodeo has already taken place — buddy group
                    entries are closed.
                  </p>
                </CardContent>
              </Card>
            );
          }
          if (entriesHaveClosed) {
            return (
              <Card>
                <CardContent className="py-6">
                  <p className="text-sm text-muted-foreground text-center">
                    Entries are closed for this rodeo. Contact the
                    organization if you need to be added.
                  </p>
                </CardContent>
              </Card>
            );
          }

          return (
            <>
        {/* Reuse one of the user's existing groups, if any. Violet
            accent stripe matches the buddy-group palette used across
            the dashboard, Event Detail, and Rodeo Detail. */}
        {otherGroups.length > 0 && (
          <Card className="relative border-2 border-violet-300 dark:border-violet-800 overflow-hidden">
            <span
              aria-hidden
              className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500/70"
            />
            <CardHeader className="pl-5">
              <CardTitle className="text-base">
                Use one of your existing buddy groups
              </CardTitle>
            </CardHeader>
            <CardContent className="pl-5 space-y-2">
              <p className="text-sm text-muted-foreground">
                These are buddy groups you&apos;re the primary cardholder of
                that aren&apos;t tied to another rodeo. Pick one to bind it to
                this rodeo.
              </p>
              <div className="space-y-2">
                {otherGroups.map((g) => {
                  const accepted = g.member_count;
                  const pending = g.pending_invitation_count ?? 0;
                  // A buddy group needs at least 2 accepted members
                  // (primary + 1 other) before it can register for a
                  // rodeo. Pending invitations don't count toward the
                  // minimum but ARE allowed — they'll join once accepted.
                  const tooFewAccepted = accepted < 2;
                  // M:N: a group can be attached to many rodeos. Mark
                  // it already-linked when this specific rodeo is in
                  // the attachment list (or the legacy single column
                  // matches, for pre-migration data).
                  const alreadyLinked = rodeoId
                    ? g.attached_rodeo_ids?.includes(rodeoId) ||
                      g.rodeo_id === rodeoId
                    : false;
                  const isExpanded = expandedInviteGroupId === g.id;
                  // Hide the inline invite affordance once the group is
                  // already at the 4-member cap (accepted + pending) —
                  // BuddyGroupManageMembers also no-ops in that case.
                  const inviteCapacity = 4 - (accepted + pending);
                  const canInvite = inviteCapacity > 0 && !!userProfileId;
                  return (
                    <div
                      key={g.id}
                      className="rounded-lg border p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-start gap-2 text-sm">
                          <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">
                              {g.group_name ?? 'Unnamed group'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {accepted} accepted member
                              {accepted !== 1 ? 's' : ''}
                              {pending > 0 ? ` · ${pending} pending` : ''}
                              {alreadyLinked
                                ? ' · already linked to this rodeo'
                                : ' · not yet attached to a rodeo'}
                            </p>
                            {(() => {
                              // Roster line — list out the actual people in
                              // the group so the user can pick the right
                              // one. Primary is tagged with "(primary)" so
                              // the cardholder is obvious. Falls back
                              // silently when the roster fetch is still in
                              // flight or has failed; the count line above
                              // already conveys size in that case.
                              const members = groupMembers[g.id];
                              if (!members || members.length === 0) return null;
                              const labels = members.map((m) => {
                                const name =
                                  m.display_name ??
                                  (m.user_profile_id === userProfileId
                                    ? 'You'
                                    : 'Unknown member');
                                return m.is_primary ? `${name} (primary)` : name;
                              });
                              return (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Members: {labels.join(', ')}
                                </p>
                              );
                            })()}
                          </div>
                        </div>
                        {alreadyLinked ? (
                          // Group is already registered for this rodeo —
                          // offer Unregister so the primary can free it
                          // (e.g. to register a different group instead).
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setWithdrawTarget({
                                id: g.id,
                                name: g.group_name ?? null,
                              })
                            }
                            disabled={attachingId === g.id}
                          >
                            {attachingId === g.id ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : null}
                            Withdraw
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2">
                            {canInvite && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  setExpandedInviteGroupId(
                                    isExpanded ? null : g.id,
                                  )
                                }
                                aria-expanded={isExpanded}
                                aria-controls={`invite-panel-${g.id}`}
                              >
                                <UserPlus className="h-4 w-4 mr-1" />
                                {isExpanded
                                  ? 'Hide invite'
                                  : tooFewAccepted
                                    ? 'Invite members'
                                    : 'Invite more'}
                                {isExpanded ? (
                                  <ChevronUp className="h-3.5 w-3.5 ml-1" />
                                ) : (
                                  <ChevronDown className="h-3.5 w-3.5 ml-1" />
                                )}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleAttach(g.id)}
                              disabled={attachingId === g.id || tooFewAccepted}
                            >
                              {attachingId === g.id ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : null}
                              Register for this rodeo
                            </Button>
                          </div>
                        )}
                      </div>
                      {tooFewAccepted && !alreadyLinked && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                          <AlertCircle className="h-3.5 w-3.5" />
                          You need at least one other accepted member before
                          this group can register. Invite members or wait for
                          your invitations to be accepted.
                        </p>
                      )}
                      {!tooFewAccepted && pending > 0 && !alreadyLinked && (
                        <p className="text-xs text-muted-foreground">
                          Only the {accepted} accepted member
                          {accepted !== 1 ? 's' : ''} (you and your accepted
                          invitees) will be considered registered. The {pending}{' '}
                          pending invitee{pending !== 1 ? 's' : ''} will join
                          automatically once they accept.
                        </p>
                      )}
                      {alreadyLinked && tooFewAccepted && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                          <AlertCircle className="h-3.5 w-3.5" />
                          This group is entered but only has 1 accepted
                          member. Withdraw it (and invite members first) or
                          replace it with a different group.
                        </p>
                      )}
                      {/* Inline invite panel — opens in place so the user
                          never has to leave the registration screen to
                          recruit teammates. Reuses the same widget the
                          rodeo-attached view uses. */}
                      {isExpanded && canInvite && userProfileId && (
                        <div id={`invite-panel-${g.id}`} className="pt-2">
                          <InlineBuddyGroupInvitePanel
                            groupId={g.id}
                            currentUserProfileId={userProfileId}
                            rodeoId={rodeoId ?? ''}
                            onInvitationsChanged={refreshOtherGroups}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="relative border-2 border-violet-300 dark:border-violet-800 overflow-hidden">
          <span
            aria-hidden
            className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500/70"
          />
          <CardHeader className="pl-5">
            <CardTitle className="text-base">
              {otherGroups.length > 0 ? 'Or create a new group' : 'Group Details'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pl-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="group-name">Group name</Label>
              <Input
                id="group-name"
                placeholder="e.g. Smith Family, Team Gold..."
                value={newGroupName}
                onChange={(e) => {
                  setCreateError(null);
                  setNewGroupName(e.target.value);
                }}
              />
              {createError && (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4" />
                  <span>{createError}</span>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={handleCreate} disabled={creating || !newGroupName.trim()}>
                {creating ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                Create buddy group
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              After creation, the new group will appear above with an
              &quot;Invite members&quot; panel opened. Once at least one
              invitee accepts you can register the group for this rodeo and
              lock the group&apos;s performance preferences.
            </p>
          </CardContent>
        </Card>
            </>
          );
        })()}
        {withdrawModal}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/rodeos/${rodeoId}`)}
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to Rodeo
      </Button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          {/* Rodeo name is the primary heading — it's the context the
              user came here to manage. The group name shifts down to a
              violet subtitle so it still reads at a glance, but the
              "I'm in [Rodeo Name]" identity is unmistakable. */}
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Buddy Group · Registered
          </p>
          <h1 className="text-3xl font-bold tracking-tight">
            {rodeo?.name ?? 'This rodeo'}
          </h1>
          <p className="text-base font-semibold text-violet-700 dark:text-violet-300">
            {group.group_name ?? 'Unnamed group'}
          </p>
          <p className="text-sm text-muted-foreground">
            Manage members and set rodeo-level performance preferences. These
            apply to every event your group members register for.
          </p>
        </div>
        {isPrimary && editable.allowed && (
          <Button variant="outline" size="sm" onClick={handleUnregister}>
            Withdraw from rodeo
          </Button>
        )}
      </div>

      {/* Members — accepted + pending merged so capacity counts both. */}
      <Card className="relative border-2 border-violet-300 dark:border-violet-800 overflow-hidden">
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500/70"
        />
        <CardHeader className="pl-5">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            Group Members
            <Badge variant="secondary">
              {group.members.length + pendingInvites.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pl-5">
          {userProfileId ? (
            <BuddyGroupManageMembers
              groupId={group.id}
              rodeoId={rodeoId ?? ''}
              members={[
                ...group.members.map((m) => ({
                  ...m,
                  invitation_status: m.invitation_status ?? ('accepted' as const),
                })),
                ...pendingInvites,
              ]}
              currentUserProfileId={userProfileId}
              isPrimaryUser={isPrimary}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Loading user…</p>
          )}
        </CardContent>
      </Card>

      {/* Edit-gating notices */}
      {!isPrimary && (
        <div className="flex items-start gap-2 rounded border border-blue-500/30 bg-blue-500/10 p-3 text-sm">
          <Lock className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-blue-700 dark:text-blue-300">Read-only view</p>
            <p className="text-blue-700/80 dark:text-blue-300/80">
              Only the primary cardholder can edit buddy group preferences and members.
            </p>
          </div>
        </div>
      )}
      {isPrimary && !editable.allowed && (
        <div className="flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
          <Lock className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-amber-700 dark:text-amber-300">
              Preferences are frozen
            </p>
            <p className="text-amber-700/80 dark:text-amber-300/80">
              {editable.reason ?? 'Draw has run; preferences can no longer be changed.'}
            </p>
          </div>
        </div>
      )}
      {lockedAt && isPrimary && editable.allowed && (
        <div className="flex items-start gap-2 rounded border border-green-500/30 bg-green-500/10 p-3 text-sm">
          <Lock className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-green-700 dark:text-green-300">
              Preferences locked for the group
            </p>
            <p className="text-green-700/80 dark:text-green-300/80">
              Saved {new Date(lockedAt).toLocaleString()}. You can still edit
              until the first draw runs for any event in this rodeo.
            </p>
          </div>
        </div>
      )}

      {/* Preferences form */}
      <Card className="relative border-2 border-violet-300 dark:border-violet-800 overflow-hidden">
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500/70"
        />
        <CardHeader className="pl-5">
          <CardTitle className="text-base">Performance Preferences</CardTitle>
        </CardHeader>
        <CardContent className="pl-5 space-y-4">
          <fieldset disabled={readOnly} className="space-y-4">
            <PerformancePreferenceStep
              performanceOptions={performanceOptions}
              firstPreference={first}
              secondPreference={second}
              onFirstChange={(v) => {
                setError(null);
                setFirst(v);
              }}
              onSecondChange={(v) => {
                setError(null);
                setSecond(v);
              }}
              preferenceConfig={rodeo?.preference_config ?? null}
              error={error ?? undefined}
            />
          </fieldset>
          {error && readOnly && (
            <div className="flex items-center gap-2 text-sm text-red-500">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
          {isPrimary && (
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={readOnly || saving || !first}>
                <Save className="h-4 w-4 mr-1" />
                {saving ? 'Saving…' : lockedAt ? 'Update preferences' : 'Lock preferences for group'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        These preferences are shared by the entire buddy group. Once set, every
        event registration any group member makes for this rodeo will use these
        values, and individual contestants can no longer override them.
      </p>
      {withdrawModal}
    </div>
  );
}
